import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/planner/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Printer } from "lucide-react";

// Packing list (GET /fill-plan/pack): what to load to bring the chosen machines' slots up to par, per
// product, with what's in backstock and what still has to be bought.
const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export default function PackList() {
  const [all, setAll] = useState([]);           // every machine, for the picker
  const [picked, setPicked] = useState(null);   // ids; null until machines load (then all)
  const [list, setList] = useState(null);
  const [packed, setPacked] = useState({});

  useEffect(() => {
    api.get("/fill-plan/pack").then((r) => { setAll(r.data.machines); setPicked(r.data.machines.map((m) => m.id)); setList(r.data); })
      .catch(() => toast.error("Couldn't load the packing list."));
  }, []);
  useEffect(() => {
    if (picked == null) return;
    if (!picked.length) { setList({ machines: [], items: [], units: 0, short: 0 }); return; }
    api.get("/fill-plan/pack", { params: { machine_ids: picked.join(",") } }).then((r) => setList(r.data))
      .catch(() => toast.error("Couldn't load the packing list."));
  }, [picked]);

  const toggle = (id) => setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const groups = useMemo(() => {
    const g = [];
    (list?.items || []).forEach((i) => {
      const last = g[g.length - 1];
      if (last && last.category === i.category) last.items.push(i); else g.push({ category: i.category, items: [i] });
    });
    return g;
  }, [list]);
  const multi = (list?.machines.length || 0) > 1;

  const print = () => {
    const rows = groups.map((g) => `<tr class="cat"><td colspan="${multi ? 5 : 4}">${esc(g.category)}</td></tr>` + g.items.map((i) =>
      `<tr><td class="chk"></td><td>${esc(i.name)}</td><td class="n"><b>${i.units}</b></td>${multi ? `<td class="m">${i.machines.map((m) => `${esc(m.name)} ${m.units}`).join(" · ")}</td>` : ""}<td class="n">${i.short ? `buy ${i.short}` : ""}</td></tr>`).join("")).join("");
    const names = list.machines.map((m) => esc(m.name)).join(", ");
    const doc = `<!doctype html><html><head><title>Packing list</title><style>
      body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#111} h1{font-size:20px;margin:0 0 2px} .sub{color:#555;font-size:12px;margin:0 0 10px}
      table{border-collapse:collapse;width:100%} td,th{border:1px solid #999;padding:6px;font-size:13px;text-align:left}
      th{background:#f2f2f2} .n{text-align:right;white-space:nowrap} .chk{width:28px} .m{font-size:11px;color:#444} tr.cat td{background:#eee;font-weight:bold;font-size:12px}
      @media print{body{margin:10mm}}</style></head><body>
      <h1>Packing list</h1><p class="sub">${new Date().toLocaleDateString()} · ${names} · ${list.units} units</p>
      <table><thead><tr><th class="chk">✓</th><th>Product</th><th class="n">Pack</th>${multi ? "<th>Per machine</th>" : ""}<th class="n">Short</th></tr></thead><tbody>${rows}</tbody></table>
      <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(doc);
    w.document.close();
  };

  if (!list || picked == null) return <Card className="p-8 text-center text-muted-foreground">Loading…</Card>;
  if (!all.length) return <Card className="p-8 text-center text-muted-foreground">Add a machine and its planogram first.</Card>;
  return (
    <div data-testid="pack-list">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Machines:</span>
        {all.map((m) => (
          <button key={m.id} type="button" onClick={() => toggle(m.id)} aria-pressed={picked.includes(m.id)} data-testid={`pack-machine-${m.id}`}
            className={`rounded-full border px-3 py-1 text-sm ${picked.includes(m.id) ? "border-primary bg-primary/10 font-medium text-primary" : "text-muted-foreground"}`}>
            {m.name}{m.location ? <span className="text-xs opacity-70"> · {m.location}</span> : null}
          </button>
        ))}
        <button type="button" className="text-xs text-primary underline" onClick={() => setPicked(picked.length === all.length ? [] : all.map((m) => m.id))}>
          {picked.length === all.length ? "None" : "All"}
        </button>
        <Button variant="outline" className="ml-auto" onClick={print} disabled={!list.items.length} data-testid="pack-print"><Printer className="mr-2 h-4 w-4" />Print</Button>
      </div>

      {!picked.length ? <Card className="p-8 text-center text-muted-foreground">Pick the machines you're filling.</Card>
        : !list.items.length ? <Card className="p-8 text-center text-muted-foreground">These machines are full: every slot is at its par.</Card> : (
        <>
          <p className="mb-2 text-sm" data-testid="pack-summary">
            <b>{list.units}</b> units for {list.machines.length} machine{list.machines.length === 1 ? "" : "s"}
            {list.short > 0 ? <span className="text-amber-600"> · {list.short} more than you have in backstock</span> : <span className="text-green-600"> · all in backstock</span>}
          </p>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pack-table">
              <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <tr><th className="w-8 p-2.5" /><th className="p-2.5">Product</th><th className="p-2.5 text-right">Pack</th>
                  {multi && <th className="p-2.5">Per machine</th>}<th className="p-2.5 text-right">Backstock</th></tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.category}>
                    <tr className="bg-muted/30"><td colSpan={multi ? 5 : 4} className="px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.category}</td></tr>
                    {g.items.map((i) => (
                      <tr key={i.product_id} className={`border-t ${packed[i.product_id] ? "opacity-50" : ""}`} data-testid="pack-row">
                        <td className="p-2.5"><input type="checkbox" checked={!!packed[i.product_id]} onChange={(e) => setPacked({ ...packed, [i.product_id]: e.target.checked })} aria-label={`Packed ${i.name}`} /></td>
                        <td className="p-2.5">
                          <div className="flex items-center gap-2">
                            {i.image_path ? <img src={fileUrl(i.image_path)} alt="" className="h-8 w-8 shrink-0 rounded bg-white object-contain" /> : null}
                            <span className={packed[i.product_id] ? "line-through" : ""}>{i.name}</span>
                          </div>
                        </td>
                        <td className="p-2.5 text-right text-base font-bold">{i.units}</td>
                        {multi && <td className="p-2.5 text-xs text-muted-foreground">{i.machines.map((m) => `${m.name} ${m.units}`).join(" · ")}</td>}
                        <td className="p-2.5 text-right">{i.short ? <span className="text-amber-600">{i.on_hand} (buy {i.short})</span> : <span className="text-muted-foreground">{i.on_hand}</span>}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Card>
          <p className="mt-2 text-xs text-muted-foreground">Counts come from each slot's par minus what's in it now. After loading, tap <b>Fill all to par</b> on each machine so its counts and your backstock update.</p>
        </>
      )}
    </div>
  );
}
